import { toast } from 'sonner';

/**
 * Safe print utility — zero innerHTML or document.write usage.
 * Uses DOMParser + adoptNode to clone sanitized nodes into the print window.
 *
 * Robustness fixes baked in (Feb 2026):
 *   - Detects popup-blocker (window.open returns null) and surfaces a toast
 *     instead of silently failing.
 *   - Calls .print() *after* the new window's load event so Chrome/Safari
 *     don't fire the dialog against an empty document. Falls back to a 200ms
 *     setTimeout for engines that don't fire 'load' on about:blank.
 *   - Lets the user close the print preview themselves; auto-closes via
 *     'afterprint' so we don't kill the dialog before it appears.
 */
export function safePrint(htmlContent) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) {
    toast.error('Pop-up blocked — please allow pop-ups for this site to print.');
    return;
  }

  const parser = new DOMParser();
  const parsed = parser.parseFromString(htmlContent, 'text/html');
  const destDoc = printWindow.document;

  // Clear existing content (about:blank has an empty head/body but be safe)
  while (destDoc.head.firstChild) destDoc.head.removeChild(destDoc.head.firstChild);
  while (destDoc.body.firstChild) destDoc.body.removeChild(destDoc.body.firstChild);

  Array.from(parsed.head.childNodes).forEach((node) => {
    destDoc.head.appendChild(destDoc.adoptNode(node.cloneNode(true)));
  });
  Array.from(parsed.body.childNodes).forEach((node) => {
    destDoc.body.appendChild(destDoc.adoptNode(node.cloneNode(true)));
  });
  Array.from(parsed.body.attributes).forEach((attr) => {
    destDoc.body.setAttribute(attr.name, attr.value);
  });

  // Auto-close the spawned tab once the OS print dialog returns (either via
  // "Print" or "Cancel"). Keep it open if the browser never fires afterprint.
  printWindow.addEventListener('afterprint', () => {
    try { printWindow.close(); } catch (e) { /* tab may already be closed */ }
  });

  // Fire the print dialog *after* the new window has rendered. Some engines
  // (notably Chrome on macOS) treat a print() call on a half-rendered doc as
  // a no-op, which is the canonical "nothing happens" symptom.
  let printed = false;
  const doPrint = () => {
    if (printed) return;
    printed = true;
    try {
      printWindow.focus();
      printWindow.print();
    } catch (e) {
      // Ignored — closed mid-flow.
    }
  };

  // Two safety nets: the load event, and a short timer as a backstop for
  // engines that don't fire 'load' on a doc populated this way.
  if (destDoc.readyState === 'complete') {
    setTimeout(doPrint, 100);
  } else {
    printWindow.addEventListener('load', () => setTimeout(doPrint, 50));
    setTimeout(doPrint, 400);
  }
}
